<template>
    <div class="container tile">
        <LoadingPlaceholder v-if="loading" />
        <div v-if="!loading" class="col-sm-6 mx-auto">
            <h1>Multi-factor Authentication Settings</h1>
            <div class="card">
                <div class="card-body">
                    <h2 class="card-title h5">{{ title }}</h2>
                    <p class="card-text">{{ description }} Enter your code below.</p>
                </div>
                <ul v-show="verifyStatus != 'success'" class="list-group list-group-flush">
                    <li class="list-group-item">
                        <p class="card-text">Code</p>
                        <form @submit.prevent="submit">
                            <div class="row">
                                <div class="col-lg-9 mb-2">
                                    <label class="sr-only" for="inputToken">Enter the multi-factor authentication code</label>
                                    <input type="text" v-model="token" id="inputToken" class="form-control" placeholder="Enter confirmation code..." required v-on:change="inputFormatValid" />
                                </div>
                                <div class="col-sm-3">
                                    <label class="sr-only" for="verifyToken">Submit the multi-factor authentication code</label>
                                    <input type="submit" value="Submit" id="verifyToken" class="btn btn-primary btn-sm w-100" />
                                </div>
                            </div>
                        </form>
                        <p class="card-text">Didn't receive a code? <button class="btn btn-link align-baseline" @click.prevent="generateToken">Try sending it again</button></p>
                    </li>
                </ul>
            </div>
            <div v-show="showResendMessage" class="alert alert-info my-3">Code has been sent.</div>
            <div v-show="verifyStatus == 'success'" class="alert alert-success mt-3">{{ verifyMessage }}</div>
            <div v-show="verifyStatus == 'failure'" class="alert alert-danger mt-3">{{ verifyMessage }}</div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, inject } from "vue";
import LoadingPlaceholder from "@/lib/LoadingPlaceholder.3.vue";
import generateTokenBase from "../../../lib/generateToken";
import getData from "@/admin/lib/getData";
import { fetchXsrf, throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { useMainStore } from "@/mainStore";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { onActivated } from "@vue/runtime-core";
interface Props {
    settings: {
        tokenType: string;
        factorType: string;
    };
}
export default defineComponent({
    components: {
        LoadingPlaceholder,
    },
    props: ["settings"],
    setup(props: Props) {
        const mainStore = useMainStore();
        const loading = ref(true);
        const title = ref("");
        const description = ref("");
        const tokenType = ref("");
        const numberValid = ref(false);
        const factorType = ref("");
        const redirectPage = ref("");
        const redirectSettings = ref(null);
        const token = ref("");
        const value = ref("");
        const verifyStatus = ref("");
        const verifyMessage = ref("");
        const showResendMessage = ref(false);

        const generateToken = async () => {
            const json = await generateTokenBase(mainStore, () => mainStore.getUrl("multiFactorAuthenticationGenerateToken"), tokenType.value, value.value);
            if (json.Success) {
                showResendMessage.value = true;
                setTimeout(() => (showResendMessage.value = false), 5000);
            }
        };
        const changePage = inject(changePageInjectKey)!;
        const submit = async () => {
            if (!numberValid.value) {
                return false;
            }
            var verifyUrl = await mainStore.getUrl("multiFactorAuthenticationVerifyTokenInput");
            const formData = new FormData();
            formData.append("tokenType", tokenType.value);
            formData.append("factorType", factorType.value);
            formData.append("token", token.value);
            formData.append("value", value.value);
            try {
                const verifyResponse = await fetchXsrf(mainStore, verifyUrl, {
                    method: "POST",
                    credentials: "same-origin",
                    body: formData,
                });
                throwIfNonSuccessStatus(verifyResponse);
                const json = await verifyResponse.json();
                if (json.Success && redirectPage.value) {
                    changePage({ page: redirectPage.value, settings: redirectSettings.value! });
                } else {
                    verifyStatus.value = json.Success ? "success" : "failure";
                    verifyMessage.value = json.Message;
                }
            } catch {
                verifyStatus.value = "failure";
                verifyMessage.value = "Code not verified. Please try entering code again.";
            }
        };
        const inputFormatValid = () => {
            const tokenReg = /^\d{6}$/;
            var trimmedToken = token.value.trim();
            if (tokenReg.test(trimmedToken)) {
                numberValid.value = true;
                verifyStatus.value = "";
                verifyMessage.value = "";
            } else {
                numberValid.value = false;
                verifyStatus.value = "failure";
                verifyMessage.value = "Please enter the 6 digit verification code";
            }
        };
        onActivated(async () => {
            loading.value = true;
            const vm = await getData(mainStore, "WisconsinDefault", () => mainStore.getUrl("multiFactorAuthenticationVerifyTokenData", { tokenType: props.settings.tokenType, factorType: props.settings.factorType }));
            title.value = vm.Title;
            description.value = vm.Description;
            tokenType.value = vm.TokenType;
            factorType.value = vm.FactorType;
            value.value = vm.Value;
            redirectPage.value = vm.RedirectPage;
            redirectSettings.value = vm.RedirectSettings;
            token.value = "";
            verifyStatus.value = "";
            verifyMessage.value = "";
            numberValid.value = false;
            loading.value = false;
        });

        const result = {
            loading,
            title,
            description,
            verifyStatus,
            submit,
            token,
            inputFormatValid,
            generateToken,
            showResendMessage,
            verifyMessage,
        };
        ("#if JEST");
        //@ts-ignore
        result.tokenType = tokenType;
        //@ts-ignore
        result.factorType = factorType;
        //@ts-ignore
        result.value = value;
        //@ts-ignore
        result.redirectPage = redirectPage;
        //@ts-ignore
        result.redirectSettings = redirectSettings;
        ("#endif");

        return result;
    },
});
</script>
